import React from "react";
import productUpdates from "../data/productUpdates.js";
import {
  getLatestAnnouncedUpdate,
  getVisibleAnnouncedUpdates,
  isUpdateUnread as resolveUpdateUnread,
} from "../utils/productUpdatesStorage.js";
import { useUserUiPreferences } from "./useUserUiPreferences.js";
import { fetchPublishedUpdates } from "../lib/productUpdatesApi.js";

const announcedUpdates = productUpdates.filter((update) => update?.announce === true && update?.announceTone === "danger");
const latestAnnouncedUpdate = getLatestAnnouncedUpdate(announcedUpdates);

export default function useProductUpdates({ loadPublished = false } = {}) {
  const [publications, setPublications] = React.useState([]);
  const [error, setError] = React.useState("");
  const reloadPublished = React.useCallback(async () => {
    try {
      const result = await fetchPublishedUpdates();
      setPublications(result.publications);
      setError("");
    } catch (failure) { setError(failure.message); }
  }, []);
  React.useEffect(() => {
    if (!loadPublished) return undefined;
    void reloadPublished();
    const refresh = () => { if (document.visibilityState !== "hidden") void reloadPublished(); };
    const interval = setInterval(refresh, 15000);
    window.addEventListener("focus", refresh);
    return () => { clearInterval(interval); window.removeEventListener("focus", refresh); };
  }, [loadPublished, reloadPublished]);
  const {
    dismissedProductUpdateIds: dismissedUpdateIds,
    dismissProductUpdate,
  } = useUserUiPreferences();

  const visibleAnnouncedUpdates = React.useMemo(() => (
    getVisibleAnnouncedUpdates(announcedUpdates, dismissedUpdateIds)
  ), [dismissedUpdateIds]);

  const visibleAnnouncedUpdate = visibleAnnouncedUpdates[0] ?? null;

  const dismissUpdate = React.useCallback((updateId = latestAnnouncedUpdate?.id) => {
    if (!updateId) {
      return;
    }

    try {
      const operation = dismissProductUpdate(updateId, { immediate: true });
      if (operation && typeof operation.catch === "function") {
        void operation.catch(() => {});
      }
    } catch {
      // Le fournisseur global rétablira la dernière préférence synchronisée.
    }
  }, [dismissProductUpdate]);

  const isUpdateUnread = React.useCallback((update) => (
    resolveUpdateUnread(update, dismissedUpdateIds)
  ), [dismissedUpdateIds]);

  return {
    publications,
    error,
    reloadPublished,
    dismissUpdate,
    dismissedUpdateId: dismissedUpdateIds[0] ?? null,
    dismissedUpdateIds,
    isUpdateUnread,
    latestAnnouncedUpdate,
    updates: productUpdates,
    visibleAnnouncedUpdate,
    visibleAnnouncedUpdates,
  };
}
